import { produce } from 'immer'
import { isNil } from 'lodash'

// Action Types
export const SET_LOADING = 'supportAccess/setLoading'
export const SET_DATA = 'supportAccess/setData'
export const SET_AUDIT_LOG = 'supportAccess/setAuditLog'
export const SET_ERROR = 'supportAccess/setError'
export const RESET = 'supportAccess/reset'

// Action Creators
export const setLoading = (payload) => ({ type: SET_LOADING, payload })
export const setData = (payload) => ({ type: SET_DATA, payload })
export const setAuditLog = (payload) => ({ type: SET_AUDIT_LOG, payload })
export const setError = (payload) => ({ type: SET_ERROR, payload })
export const reset = () => ({ type: RESET })

// State
export const initialState = {
  supportAccessUntil: null,
  isOpen: false,
  expiresInSeconds: null,
  isLoading: false,
  error: null,
  windowOrgId: null,
  auditLog: [],
}

export default function supportAccessReducer(state = initialState, action) {
  if (action.type === RESET) {
    return initialState
  }
  return produce(state, (newState) => {
    switch (action.type) {
      case SET_LOADING:
        newState.isLoading = action.payload
        break
      case SET_DATA: {
        const payload = action.payload ?? {}
        const until = payload.support_access_until ?? null
        // core-service returns `support_access_until` verbatim once the window
        // lapses, so a present timestamp does not mean the window is open.
        // Trust the server's `is_open`/`expires_in_seconds`, and fall back to
        // the expiry comparison for locally-built payloads (the DELETE path)
        // that carry no server state.
        const derivedSeconds = until
          ? Math.max(0, Math.floor((new Date(until).getTime() - Date.now()) / 1000))
          : 0
        const isOpen = isNil(payload.is_open) ? derivedSeconds > 0 : Boolean(payload.is_open)

        newState.supportAccessUntil = until
        newState.isOpen = isOpen
        if (isOpen) {
          newState.expiresInSeconds = payload.expires_in_seconds ?? derivedSeconds
        } else {
          newState.expiresInSeconds = null
        }
        if (payload.orgId !== undefined) {
          newState.windowOrgId = payload.orgId
        }
        newState.error = null
        break
      }
      case SET_AUDIT_LOG:
        newState.auditLog = action.payload ?? []
        break
      case SET_ERROR:
        newState.error = action.payload
        newState.isLoading = false
        break
      default:
        break
    }
  })
}

/**
 * Returns true when a support access window is currently open for the given orgId.
 * Only Balto admins (org 1) can have an active support window.
 *
 * @param {object} state - Redux root state
 * @param {number|null} orgId - The org being rendered
 * @returns {boolean}
 */
export const selectSupportWindowOpenForOrg = (state, orgId) => {
  // Default to {} so consumers (and partial test mock stores) that haven't
  // registered the slice get a clean `false` rather than a destructure crash.
  const { isOpen, supportAccessUntil, windowOrgId } = state.supportAccess || {}
  if (!orgId || !isOpen || !supportAccessUntil) {
    return false
  }
  if (windowOrgId !== orgId) {
    return false
  }
  return new Date(supportAccessUntil) > new Date()
}
